import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAppContext } from '../../../contexts/AppContext';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useStripeBilling } from './useStripeBilling';
import { useBillingData } from './useBillingData';
import { useInvoices } from './useInvoices';
import { getTrialDaysRemaining, getPlanLabel } from '../../../domain/entities/Subscription';
import { updateSubscriptionQuantity } from '../../../services/modules/subscription/subscription.api';
import { getOrganizationMembers } from '../../../services/modules/organizationService';
import { notify } from '../../../utils/toast';

// Preços por plano (fallback quando Stripe não retorna)
const PLAN_PRICES: Record<string, number> = {
    'basic': 59.90,
    'pro': 89.90,
    'enterprise': 99.90,
};

interface TrialInfo {
    isTrialing: boolean;
    daysLeft: number;
    expirationDate: string | null;
}

export const useBillingPage = () => {
    const { setCurrentPage } = useAppContext();
    const { currentOrganization } = useOrganization();
    const { billingContact, subscriptionPlans } = useBillingData();
    const {
        billingInfo,
        isLoading,
        openCustomerPortal,
        isPortalLoading,
        hasActiveSubscription,
        refetch
    } = useStripeBilling();

    const { latestInvoice: dbLatestInvoice } = useInvoices();

    // Estados locais
    const [membersCount, setMembersCount] = useState(1);
    const [isMembersLoading, setIsMembersLoading] = useState(true);
    const [isLicensesModalOpen, setIsLicensesModalOpen] = useState(false);
    const [isUpdatingLicenses, setIsUpdatingLicenses] = useState(false);

    // Buscar contagem de membros
    useEffect(() => {
        if (currentOrganization?.id) {
            setIsMembersLoading(true);
            getOrganizationMembers(currentOrganization.id)
                .then((members) => {
                    setMembersCount(members.length);
                    setIsMembersLoading(false);
                })
                .catch(() => {
                    setIsMembersLoading(false);
                });
        }
    }, [currentOrganization?.id]);

    // Extrair dados do billing
    const { subscription, customer } = billingInfo || {};

    // Calcular informações do trial
    const getTrialInfo = useCallback((): TrialInfo => {
        const orgSubStatus = currentOrganization?.subscriptionStatus;

        // Plano pago confirmado no banco — não exibir trial genérico da org
        if (orgSubStatus === 'active') {
            return { isTrialing: false, daysLeft: 0, expirationDate: null };
        }

        // Se tem trial ativo no Stripe
        if (subscription?.isTrialing && subscription?.trialEnd) {
            const daysLeft = Math.max(0, Math.ceil(
                (new Date(subscription.trialEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
            ));
            return { isTrialing: true, daysLeft, expirationDate: subscription.trialEnd };
        }

        // Fallback: trial local registrado na organização
        const orgTrialEndsAt = currentOrganization?.trialEndsAt;
        if (orgSubStatus === 'trialing' && orgTrialEndsAt) {
            const trialEndDate = typeof orgTrialEndsAt === 'string' ? orgTrialEndsAt : orgTrialEndsAt.toISOString();
            const daysLeft = getTrialDaysRemaining(new Date(trialEndDate));
            return { isTrialing: true, daysLeft, expirationDate: trialEndDate };
        }

        return { isTrialing: false, daysLeft: 0, expirationDate: null };
    }, [subscription, currentOrganization]);

    const trialInfo = getTrialInfo();

    // Cálculos de preço dinâmicos (Stripe > Banco de Dados > Hardcoded Fallback)
    const currentPlanKey = (subscription?.planName?.toLowerCase() || currentOrganization?.plan || 'basic') as string;

    const dynamicPlan = subscriptionPlans?.find(p => p.name.toLowerCase() === currentPlanKey);
    const dynamicPrice = dynamicPlan ? parseFloat(dynamicPlan.price.replace(',', '.')) : 0;

    // Preferência de fonte de preço:
    // 1. O que vem explícito da assinatura ativa no Stripe (se existir)
    // 2. O preço dinâmico carregado do banco de dados (tabela subscription_plans)
    // 3. O fallback estrito local PLAN_PRICES
    const pricePerUnit = subscription?.pricePerUnit || dynamicPrice || PLAN_PRICES[currentPlanKey] || PLAN_PRICES['pro'];

    const quantity = Math.max(1, subscription?.quantity || membersCount || 1);
    const totalPrice = pricePerUnit * quantity;

    // Verificar se pode cancelar (não está em trial)
    const canCancelSubscription = !trialInfo.isTrialing;

    // Handlers
    const handleManageSubscription = useCallback(() => {
        if (hasActiveSubscription) {
            openCustomerPortal();
        } else {
            setCurrentPage('subscription');
        }
    }, [hasActiveSubscription, openCustomerPortal, setCurrentPage]);

    const handleManageLicenses = useCallback(() => {
        // Sem assinatura Stripe (ex.: trial local) update-subscription falha — manda assinar
        if (!hasActiveSubscription) {
            setCurrentPage('subscription');
            notify.info('Assine um plano para gerenciar licenças.');
            return;
        }
        setIsLicensesModalOpen(true);
    }, [hasActiveSubscription, setCurrentPage]);

    const handleCloseLicensesModal = useCallback(() => {
        setIsLicensesModalOpen(false);
    }, []);

    const handleUpdateLicenses = useCallback(async (newQuantity: number) => {
        setIsUpdatingLicenses(true);
        try {
            if (!currentOrganization?.id) {
                notify.error('Organização não selecionada.');
                return;
            }
            const result = await updateSubscriptionQuantity(currentOrganization.id, newQuantity);
            
            if (result.success) {
                setIsLicensesModalOpen(false);
                
                if (result.type === 'upgrade') {
                    notify.success(result.message);
                } else if (result.type === 'downgrade') {
                    notify.info(result.message);
                } else {
                    notify.success(result.message);
                }
                
                // Atualizar dados após alteração
                refetch();
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Erro ao atualizar licenças';
            notify.error(message);
        } finally {
            setIsUpdatingLicenses(false);
        }
    }, [refetch]);

    // Dados do plano
    const planName = subscription?.planName
        || getPlanLabel(currentOrganization?.plan ?? 'basic');
    const customerName = customer?.name || billingContact?.name || 'Sua Conta';

    return {
        // Estados de loading
        isLoading,
        isPortalLoading,
        isUpdatingLicenses,

        // Dados do plano
        customerName,
        planName,
        quantity,
        pricePerUnit,
        totalPrice,
        membersCount,

        // Trial
        trialInfo,
        canCancelSubscription,

        // Subscription
        subscription,
        hasActiveSubscription,
        currentPeriodEnd: subscription?.currentPeriodEnd || null,

        // Billing info
        billingInfo,
        billingContact,
        dbLatestInvoice,
        isMembersLoading,

        // Modal de licenças
        isLicensesModalOpen,
        handleManageLicenses,
        handleCloseLicensesModal,
        handleUpdateLicenses,

        // Outros handlers
        handleManageSubscription,
        openCustomerPortal,
    };
};

export default useBillingPage;
